import { useCallback, useMemo, useState } from 'react'

interface UnmountableDialogProps {
  open: boolean
  onClose: () => void
  TransitionProps: {
    onExited: () => void
  }
}

interface UnmountableProps {
  closeDialog: () => void
  dialogProps: UnmountableDialogProps
}

/**
 * Custom hook to manage the state of a dialog component.
 * This hook allows the dialog to be unmounted on the onClose event without disturbing the closing animation,
 * meaning it support conditional rendering and only load when needed.
 * @param onClose - Callback function to call when the dialog is closed (from parent component)
 * @returns Props to spread onto the Dialog component and the function to close the dialog
 */
export const useUnmountableDialog = (onClose: () => void, disabled = false): UnmountableProps => {
  const [isOpen, setIsOpen] = useState(true)

  const handleClose = useCallback(() => {
    if (!disabled) setIsOpen(false)
  }, [disabled])

  const handleExited = useCallback(() => {
    // Only call parent onClose when animation is complete
    if (!disabled) onClose()
  }, [onClose, disabled])

  return useMemo<UnmountableProps>(
    () => ({
      closeDialog: handleClose,
      dialogProps: {
        open: isOpen,
        onClose: handleClose,
        TransitionProps: {
          onExited: handleExited,
        },
      },
    }),
    [handleClose, handleExited, isOpen],
  )
}
